/**
 * Login / lobby view (S0): guest login, create a session, join by id/code, and browse public
 * sessions. Everything is driven from the single terminal input bar (keyboard-operable). It reads
 * the {@link Store}/{@link AppApi} and emits intents; it never touches the network.
 */
import type { TermLine } from '../term/index.ts';
import { versionReadout } from './sessionView.ts';
import type { AppApi } from '../shell/appApi.ts';
import type { ScenarioCard, SessionListItem } from '../../core/index.ts';
import { t } from '../i18n.ts';
import { complete, complete_ as c } from '../../core/index.ts';
import type { CommandSpec, Completion } from '../../core/index.ts';

const HELP_KEYS = [
  'login.help.login',
  'login.help.create',
  'login.help.decks',
  'login.help.join',
  'login.help.list',
  'login.help.number',
  'login.help.diag',
  'login.help.reload',
  'login.help.help',
  'login.help.share',
] as const;

/** The help block in the player's language. Column alignment lives in the catalog, per language. */
function help(): string[] {
  return [t('login.commands'), ...HELP_KEYS.map((k) => `  ${t(k)}`)];
}

/**
 * The lobby's list as ONE numbered sequence: your sessions, then the public directory (DEC-18).
 *
 * Both the rendering and the join-by-number handler count from this, which is the point. Two places
 * deriving the same ordering independently is how a number on screen and a number typed come to mean
 * different rooms - and the failure would be silent and would join you to a stranger's session.
 */
export function listed(app: AppApi): SessionListItem[] {
  return [...app.mySessions(), ...app.publicSessions()];
}

/**
 * The deck a `create ... game <deck>` names, or null when it names nothing the catalog has (DEC-17 M4
 * increment 8b). Accepts a bare id or a full `id@version`, because a player who read `growth-squeeze@1`
 * off the list will type it back.
 */
export function findDeck(decks: readonly ScenarioCard[], token: string): ScenarioCard | null {
  const wanted = token.trim();
  return decks.find((d) => d.ref === wanted) ?? decks.find((d) => d.id === wanted) ?? null;
}

/** One deck as a line a player can read and then type back. */
export function deckLine(deck: ScenarioCard): string {
  return t('login.deckLine', {
    ref: deck.ref,
    title: deck.title,
    // The FRAME, in the deck's own words rather than inferred from a missing deadline: an excerpt is a
    // fixed-length scenario and a sim is a company you run until it stops.
    shape:
      deck.frame === 'sim'
        ? t('login.deckSim')
        : t('login.deckExcerpt', { rounds: deck.deadline ?? 0 }),
    seats: deck.seats,
  });
}

export function renderLogin(app: AppApi): TermLine[] {
  const lines: TermLine[] = [];
  lines.push({ text: t('login.title'), cls: 'title' });
  lines.push({
    text: t('login.status', {
      connection: app.connectionStatus(),
      player: app.playerId() ?? t('login.notSignedIn'),
      version: versionReadout(app),
    }),
    cls: 'dim',
  });
  lines.push({ text: '' });

  if (!app.isAuthenticated()) {
    lines.push({ text: t('login.begin'), cls: 'sys' });
  } else {
    lines.push({ text: t('login.signedIn'), cls: 'sys' });
  }
  lines.push({ text: '' });

  // SECTIONED, MINE FIRST (DEC-18). "Where was I?" is answered before "what's out there?", because
  // returning is the common case and browsing is the rare one. The numbering runs across BOTH sections
  // so join-by-number stays one gesture - see `listed()`, which is the single source both this and the
  // command handler count from, so a number on screen and a number typed can never disagree.
  const mine = app.mySessions();
  const sessions = app.publicSessions();

  const row = (s: SessionListItem, n: number): string =>
    `  ${String(n).padStart(2)}. ${s.name}  [${s.playerCount}${
      s.maxPlayers ? '/' + s.maxPlayers : ''
    }]  ${s.status}  ${s.id}`;

  if (mine.length) {
    lines.push({ text: t('login.mySessions'), cls: 'dim' });
    mine.forEach((s, i) => lines.push({ text: row(s, i + 1) }));
    lines.push({ text: '' });
  }

  if (sessions.length) {
    lines.push({ text: t('login.publicSessions'), cls: 'dim' });
    sessions.forEach((s, i) => lines.push({ text: row(s, mine.length + i + 1) }));
  } else {
    // Only worth saying when there is nothing at all. With your own sessions listed above, "the
    // directory is empty" is a fact about strangers' rooms and not an answer anybody was waiting for.
    lines.push({
      text: mine.length ? t('login.noPublicOthers') : t('login.noPublicSessions'),
      cls: 'dim',
    });
  }
  lines.push({ text: '' });

  for (const h of help()) lines.push({ text: h, cls: 'dim' });
  lines.push({ text: '' });

  for (const entry of app.log()) lines.push({ text: entry, cls: 'log' });
  return lines;
}

/**
 * The lobby's completions (tab-completion.md §3 step 4). Its commands are bare words rather than slash
 * commands, so the table is small and local; there is no projection here to complete ids from.
 */
const LOGIN_COMMANDS: ReadonlyMap<string, CommandSpec> = new Map<string, CommandSpec>([
  ['login', { usage: 'login', args: [] }],
  // `pub` first: it is the flag with a consequence now (DEC-18 - publishing is the deliberate act).
  // Position 2 is the flag word; position 3 is the deck `game` takes, completed from the catalog the
  // server listed rather than from a hard-coded set (the "never from an authored list" rule).
  [
    'create',
    {
      usage: 'create <name> [game [deck]] [pub]',
      args: [],
      // NOT positional: the name is free text of any word count, so what completes depends on the word
      // BEFORE the caret rather than on how many words came first. After `game` that is the deck
      // catalog the server listed; everywhere else it is the two flags.
      rest: (fold, argv, i) => (argv[i - 1] === 'game' ? [...fold.decks] : ['game', 'pub', 'priv']),
    },
  ],
  ['decks', { usage: 'decks', args: [] }],
  ['join', { usage: 'join', args: [c.sessionId] }],
  ['list', { usage: 'list', args: [] }],
  ['share', { usage: 'share', args: [] }],
  ['diag', { usage: 'diag', args: [] }],
  ['reload', { usage: 'reload', args: [] }],
  ['help', { usage: 'help', args: [] }],
]);

export function loginCompletion(app: AppApi, text: string, caret: number): Completion {
  // The lobby's words carry no leading slash, so the shared engine (which keys on `/`) is fed one and
  // the answer is unwrapped. Cheaper than a second tokenizer, and it cannot drift from the session one.
  const slashed = text.startsWith('/') ? text : `/${text}`;
  const offset = text.startsWith('/') ? 0 : 1;
  const found = complete({
    commands: LOGIN_COMMANDS,
    // The lobby completes the ids the server just listed - the same "never from an authored list" rule
    // the session view follows, with the public list standing in for the projection.
    fold: {
      game: null,
      panels: [],
      sessions: listed(app).map((s) => s.id),
      decks: app.scenarios().map((d) => d.id),
      // The lobby has no session, so nothing can be waiting to send.
      pending: [],
    },
    text: slashed,
    caret: caret + offset,
  });

  return { items: found.items, from: Math.max(0, found.from - offset) };
}

export function loginCommand(app: AppApi, raw: string): void {
  const text = raw.trim();
  if (!text) return;
  const [cmd, ...rest] = text.split(/\s+/);
  const arg = rest.join(' ');

  const asIndex = Number(text);
  if (Number.isInteger(asIndex) && asIndex >= 1) {
    // Spans both sections, from the same ordering the view rendered (DEC-18).
    const target = listed(app)[asIndex - 1];
    if (target) void app.joinSession(target.id);
    else app.print(t('login.noSuchListed', { n: asIndex }));
    return;
  }

  switch (cmd) {
    case 'login':
      void app.guestLogin(arg);
      break;
    case 'create': {
      // Trailing flags: "pub" (publish to the directory), "game" (run the signal-triage scenario),
      // and "priv", kept as an accepted alias.
      //
      // PRIVATE IS THE DEFAULT (DEC-18), so this reads inverted from how it used to: publishing is now
      // the deliberate act. `priv` still works and still means private - it is simply redundant now.
      // Keeping it is the point rather than clutter: an invocation somebody has in their fingers must
      // not silently start meaning something else, and here it cannot, because its old meaning became
      // the default rather than the opposite.
      const words = [...rest];
      const flags = new Set<string>();
      while (words.length && (words.at(-1) === 'priv' || words.at(-1) === 'pub')) {
        flags.add(words.pop()!);
      }

      // DEC-17 M4 increment 8b: `game` may now be followed by a deck. Everything before `game` is the
      // name, everything after it is the deck a player picked off `decks`.
      //
      // BARE `game` STILL MEANS signal-triage@1, and that is deliberate rather than lazy: an
      // invocation somebody has in their fingers must not silently start meaning something else. It is
      // the same rule DEC-18 applied to `priv` in the comment above - the old spelling keeps its old
      // meaning, and the new capability is reached by typing something new.
      let deckToken: string | null = null;
      const gameAt = words.lastIndexOf('game');
      if (gameAt >= 0) {
        flags.add('game');
        deckToken =
          words
            .slice(gameAt + 1)
            .join(' ')
            .trim() || null;
        words.length = gameAt;
      }

      let scenarioRef: string | undefined;
      if (flags.has('game')) {
        if (deckToken === null) {
          scenarioRef = 'signal-triage@1';
        } else {
          const deck = findDeck(app.scenarios(), deckToken);
          if (!deck) {
            // A problem message names the action to take next (standards/messages.md): the player needs
            // to know these exist and how to see them, not just that this one did not.
            app.print(t('login.unknownDeck', { deck: deckToken }));
            break;
          }

          scenarioRef = deck.ref;
        }
      }

      const name = words.join(' ') || 'Untitled session';
      void app.createSession(name, flags.has('pub') ? 'public' : 'private', scenarioRef);
      break;
    }
    case 'join':
      if (!arg) app.print(t('login.usage.join'));
      else void app.joinSession(arg);
      break;
    case 'list':
      void app.refreshSessions();
      break;
    case 'decks': {
      // The catalog, readable and typeable (DEC-17 M4 increment 8b). Before this the only startable
      // deck was the one both clients hard-coded, and the other four were unreachable content.
      const decks = app.scenarios();
      if (!decks.length) {
        app.print(t('login.noDecks'));
        break;
      }

      app.print([t('login.decks'), ...decks.map(deckLine)].join('\n'));
      break;
    }
    case 'share':
      app.shareSession();
      break;
    case 'diag':
    case 'diagnostics':
      app.openDiagnostics();
      break;
    case 'reload':
      app.applyUpdate();
      break;
    case 'help':
      app.print(help().join('\n'));
      break;
    default:
      // A slash command in the lobby is someone who thinks they are in a session - which, after a warm
      // start, they reasonably do: the client is holding their session and cannot show it until they
      // sign back in. "Unknown command" answers the wrong question.
      app.print(
        text.startsWith('/')
          ? t('login.sessionCommand', { cmd: (cmd ?? '').replace(/^\//, '') })
          : t('login.unknown', { cmd: cmd ?? '' }),
      );
  }
}
